import { Mail } from 'lucide-react';
import { MarketingShell } from './MarketingShell';
import { useCms, cmsText, cmsList } from '../../api/cms';
import { SINGLE_FALLBACKS, LIST_FALLBACKS } from '../../content/fallbacks';

export interface LegalSection {
  heading: string;
  body: string[];
}

/**
 * Shared layout for the legal pages (privacy, terms, refund, cookies). Editorial,
 * readable measure, clear numbered hierarchy, a sticky on-this-page index on
 * desktop, and section anchors. Light + dark. Content is data so it maps cleanly
 * to the CMS later (WS5b).
 */
function slug(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

/**
 * `kind` binds the page to its CMS sections:
 *   legal.<kind>Meta  (SINGLE) — title / last-updated / intro
 *   legal.<kind>      (LIST)   — the numbered sections
 * Built-in copy lives in content/fallbacks.ts, so the live page and the admin
 * editor's placeholder can never drift. Nothing saved → the fallbacks render,
 * exactly as before this became editable.
 */
export function LegalPage({ kind }: { kind: 'terms' | 'privacy' | 'refund' | 'cookies' }) {
  const { data: cms } = useCms();
  const metaKey = `legal.${kind}Meta`;
  const meta = SINGLE_FALLBACKS[metaKey]!;
  const title = cmsText(cms, metaKey, 'title', meta.title!);
  const updated = cmsText(cms, metaKey, 'updated', meta.updated!);
  const intro = cmsText(cms, metaKey, 'intro', meta.intro!);
  // cmsList returns loose CMS items: an admin-saved section can be missing a field, or
  // carry `body` as a single string rather than an array. Normalize to LegalSection here
  // so the render below can stay simple and can never crash on a half-filled entry.
  const sections: LegalSection[] = cmsList(cms, `legal.${kind}`, LIST_FALLBACKS[`legal.${kind}`]!).map((item) => ({
    heading: typeof item.heading === 'string' ? item.heading : '',
    body: Array.isArray(item.body)
      ? item.body.filter((p): p is string => typeof p === 'string')
      : typeof item.body === 'string' && item.body.trim() !== ''
        ? [item.body]
        : [],
  }));

  return (
    <MarketingShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <header className="border-b border-zinc-200 dark:border-zinc-800 pb-7">
          <p className="text-xs font-semibold uppercase tracking-wider text-brand dark:text-brand-dm">Legal</p>
          <h1 className="mt-2 text-3xl sm:text-5xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 text-balance">
            {title}
          </h1>
          <p className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">Last updated {updated}</p>
          <p className="mt-5 text-lg text-zinc-600 dark:text-zinc-300 leading-relaxed max-w-2xl text-pretty">{intro}</p>
        </header>

        <div className="mt-10 grid lg:grid-cols-[14rem_1fr] gap-10 lg:gap-14">
          {/* On-this-page index (desktop) */}
          <nav aria-label="On this page" className="hidden lg:block">
            <div className="sticky top-24">
              <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">On this page</p>
              <ol className="mt-3 space-y-2.5">
                {sections.map((s, i) => (
                  <li key={s.heading}>
                    <a
                      href={`#${slug(s.heading)}`}
                      className="group flex gap-2.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-950 dark:hover:text-zinc-50 transition-colors"
                    >
                      <span className="tabular-nums text-zinc-400 dark:text-zinc-600 group-hover:text-brand dark:group-hover:text-brand-dm transition-colors">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="leading-snug">{s.heading}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          </nav>

          {/* Body */}
          <article className="min-w-0 max-w-2xl space-y-10">
            {sections.map((s, i) => (
              <section key={s.heading} id={slug(s.heading)} className="scroll-mt-24">
                <h2 className="text-lg sm:text-xl font-semibold text-zinc-950 dark:text-zinc-50 tracking-tight">
                  <span className="text-zinc-400 dark:text-zinc-500 tabular-nums mr-2.5">{i + 1}.</span>
                  {s.heading}
                </h2>
                <div className="mt-3 space-y-3.5">
                  {s.body.map((p, j) => (
                    <p key={j} className="text-[15px] text-zinc-600 dark:text-zinc-300 leading-relaxed">{p}</p>
                  ))}
                </div>
              </section>
            ))}

            <footer className="mt-4 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white/60 dark:bg-zinc-900/40 p-5 flex items-start gap-3 text-sm text-zinc-600 dark:text-zinc-300">
              <span className="w-9 h-9 shrink-0 grid place-items-center rounded-lg bg-brand-subtle text-brand dark:bg-brand-dm-subtle dark:text-brand-dm">
                <Mail className="w-4 h-4" aria-hidden="true" />
              </span>
              <span className="leading-relaxed pt-1">
                Questions? Email{' '}
                <a href="mailto:support@itcitysoft.com" className="font-medium text-brand dark:text-brand-dm hover:underline">
                  support@itcitysoft.com
                </a>{' '}
                or write to ITCity, Sylhet, Bangladesh.
              </span>
            </footer>
          </article>
        </div>
      </div>
    </MarketingShell>
  );
}
